import { Box, Button, Stack, Typography } from '@mui/material';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Link as ReactLink } from 'react-router-dom';

import { SubscriptionItem, useSubscriptionsInfo, useUpcomingInvoice } from '@/api/subscriptions';
import { DisplayCurrency } from '@/components/DisplayCurrency';
import { Timestamp } from '@/components/data/Timestamp';
import { RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';
import { boldWeight, cardBgLightGrey, headerBgLightBlue } from '@/theme';
import { invoiceLabels } from '@/utils';
import { itemQuantity } from '@/utils/languageHelpers';

export const CurrentSubscriptionTable = () => {
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    const { data: upcomingInvoice } = useUpcomingInvoice({
        subscriptionId: subscriptionInfo?.data[0]?.id,
    });

    /*
        Not rendering anything while loading or on error since these are handled by the summary box above us on the payments page.
    */
    if (!subscriptionInfo?.data?.length) {
        return false;
    }

    const isPastDue = subscriptionInfo.data[0].status === 'past_due';

    return (
        <>
            {subscriptionInfo.data.map((subscription) => {
                const isSubscriptionActive = subscription.status === 'active';
                // if it's not active, it's because it's past due or cancelled
                const totalDueCaption = isSubscriptionActive ? 'Total due on next invoice' : 'Total due';
                return (
                    <Stack key={subscription.id}>
                        <Stack
                            direction="row"
                            sx={{
                                alignItems: 'center',
                                width: 1,
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 1,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Typography variant="h3">Current subscription</Typography>
                            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                                <Typography>
                                    Current invoice period:{' '}
                                    <Timestamp value={subscription.current_period_start} unit="date" type="medium" />
                                    {' - '}
                                    <Timestamp value={subscription.current_period_end} unit="date" type="medium" />
                                </Typography>

                                <Button component={ReactLink} sx={{}} to="/billing" variant="text">
                                    View all invoices
                                </Button>
                            </Stack>
                        </Stack>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '20% 10% 10% 35% 25%',
                                border: '1px solid',
                                borderColor: 'divider',
                                '& > *': {
                                    alignContent: 'center',
                                },
                                '.firstColumn': {
                                    pl: 3,
                                },
                                '.totalColumn': {
                                    pr: 3,
                                    textAlign: 'end',
                                    '& > *': { justifySelf: 'end' },
                                },
                                '.itemRowTopLine': {
                                    pt: 3,
                                    pb: 1,
                                    borderTop: '1px solid',
                                    borderColor: 'inherit',
                                },
                                '.itemRowBottomLine': {
                                    pb: 3,
                                },
                                '.headerRow': {
                                    py: 2,
                                    bgcolor: headerBgLightBlue,
                                    fontWeight: boldWeight,
                                },
                                '.footerRow': {
                                    py: 3,
                                    borderTop: '1px solid',
                                    borderColor: 'inherit',
                                    bgcolor: cardBgLightGrey,
                                    fontWeight: boldWeight,
                                },
                            }}
                        >
                            <Typography sx={{ gridColumn: 'span 2' }} className="headerRow firstColumn">
                                Base price
                            </Typography>
                            <Typography sx={{ gridColumn: 'span 2' }} className="headerRow">
                                Quantity
                            </Typography>
                            <Typography className="headerRow totalColumn">Monthly subtotal</Typography>
                            {subscription.items.map((item) => (
                                <SubscriptionItemRow key={item.id} item={item} isPastDue={isPastDue} />
                            ))}
                            {!!upcomingInvoice?.data?.discounts?.length && (
                                <>
                                    <Typography
                                        variant="h5"
                                        className="itemRowTopLine firstColumn"
                                        sx={{ gridColumn: 'span 5' }}
                                    >
                                        Discounts
                                    </Typography>
                                    {upcomingInvoice?.data?.discounts?.map(
                                        ({ id, currency, amount_off, coupon_name }) => (
                                            <Typography className="totalColumn" sx={{ gridColumn: 'span 5' }} key={id}>
                                                {coupon_name}:
                                                <DisplayCurrency
                                                    sx={{ ml: 2 }}
                                                    value={-amount_off}
                                                    cents
                                                    currency={currency}
                                                />
                                            </Typography>
                                        )
                                    )}
                                </>
                            )}
                            {!isNil(upcomingInvoice?.data?.total) && (
                                <>
                                    <Typography
                                        variant="h5"
                                        className="firstColumn footerRow"
                                        sx={{ gridColumn: 'span 4' }}
                                    >
                                        {totalDueCaption}:
                                    </Typography>
                                    <Box className="footerRow totalColumn">
                                        <DisplayCurrency
                                            variant="total"
                                            value={upcomingInvoice?.data?.total}
                                            cents
                                            currency={subscription.items?.[0]?.currency}
                                        />
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Stack>
                );
            })}
        </>
    );
};

const SubscriptionItemRow = ({ item, isPastDue }: { item: SubscriptionItem; isPastDue: boolean }) => {
    const { amount, currency, quantity, interval, price_lookup_key } = item;
    const { productTitle, quantityItemName } = useMemo(() => invoiceLabels(item), [item]);

    const isRecurringSatCredits = price_lookup_key === RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY;

    return (
        <>
            <Typography variant="h5" className="itemRowTopLine firstColumn" sx={{ gridColumn: 'span 5' }}>
                {productTitle}
            </Typography>
            <Typography className="firstColumn itemRowBottomLine">
                <DisplayCurrency value={amount} cents currency={currency} sx={{ display: 'inline-block' }} /> /{' '}
                {interval}
            </Typography>
            <Typography className="itemRowBottomLine" sx={{ justifySelf: 'center' }}>
                x
            </Typography>
            <Typography className="itemRowBottomLine">{itemQuantity(quantity, quantityItemName)}</Typography>
            <Box className="itemRowBottomLine">
                {!isPastDue &&
                    (isRecurringSatCredits ? (
                        <Button
                            sx={{ py: '2px', my: '-2px' }}
                            component={ReactLink}
                            to="/payment/satelliteCredits/recurring"
                            state={{ transactionBackTarget: 'payment' }}
                            variant="text"
                        >
                            Update recurring credits
                        </Button>
                    ) : (
                        <Button sx={{ py: '2px', my: '-2px' }} component={ReactLink} to="/telemetry" variant="text">
                            Change telemetry
                        </Button>
                    ))}
            </Box>
            <Typography className="totalColumn itemRowBottomLine">
                <DisplayCurrency value={amount * quantity} cents currency={currency} />
            </Typography>
        </>
    );
};
